/**
 * API submission logic
 */

import { formatBirthday } from '../utils/dateHelpers.js';
import { t } from '../../i18n.js';
import { getTurnstileToken, resetTurnstile } from './turnstile.js';
import { trackError } from '../analytics/signupAnalytics.js';
import { getStepForApiField } from '../steps.js';
import { clearJwtToken, getJwtToken, setJwtToken } from '../../jwtTokenManager.js';
import { refusedAccountError } from '../refusedAccount.js';
import { recordSignupCapture } from '../../geo-observation.js';

export async function submitSignup(state) {
    const birthday = formatBirthday(state.day, state.month, state.year);

    // An absent token is sent as no field. The backend logs it as STATE_MISSING
    // and accepts the submit.
    const turnstileToken = getTurnstileToken();

    // The server sets captchaImageUrl only for a visitor from China. The answer travels in googleResponse.
    const captchaAnswer = state.captchaImageUrl
        // NFKC folds full-width letters and digits from a CJK input method to ASCII.
        ? (state.captchaAnswer || '').normalize('NFKC').trim()
        : '';

    const signupData = {
        name: state.name,
        email: state.email,
        birthday: birthday,
        sex: state.sex,
        username: state.username,
        // Trimmed so the submitted value is the one the step validated, and
        // the one the server stores after its own trim.
        password: (state.password || '').trim(),
        city_id: state.cityId,
        home_city_id: state.homeCityId,
        lang: window.__i18nConfig?.locale || 'en_US'
    };

    if (captchaAnswer) {
        signupData.googleResponse = captchaAnswer;
    }

    if (turnstileToken) {
        signupData['cf-turnstile-response'] = turnstileToken;
    }

    try {
        const result = await window.ApiClient.createAccount(signupData);

        if (result.token) {
            storeAuthToken(result);
        }

        // Fire the buffered device-geo observation now that the JWT is stored.
        // Fire-and-forget (keepalive) so it never delays the redirect.
        reportGeoObservation(state, result.account?.id);

        return { success: true, result };

    } catch (error) {
        console.error('[Signup] API error:', error);

        // A token is single-use, so the next attempt needs a fresh one. A
        // token minted while the POST ran is unused and stays. A POST without
        // a token consumed nothing, so the widget keeps its state.
        if (turnstileToken && getTurnstileToken() === turnstileToken) {
            resetTurnstile();
        }

        if (error.validationErrors) {
            reportServerValidationErrors(error.validationErrors, state.currentStep);

            return {
                success: false,
                validationErrors: error.validationErrors,
                // The fields can change while the POST is out. A refusal answers these values.
                sent: { email: signupData.email, username: signupData.username },
            };
        }

        // A 429 is a user outcome, a 5xx is not.
        trackError(state.currentStep, '', error.status ? `request_failed_${error.status}` : 'request_failed');

        const refused = refusedAccountError(error);
        if (refused) {
            return { success: false, error: refused.msg, offersSignIn: true };
        }

        // Only a 4xx answer carries a message for the user. A 5xx or a network error does not.
        return {
            success: false,
            error: (error.status < 500 && error.message) || t('auth.error_something_wrong')
        };
    }
}

// One event per rejected field, so each blocking field is countable on its own.
// A parameter with no step (captcha, spam) reports the step the submit left.
function reportServerValidationErrors(validationErrors, submitStep) {
    Object.keys(validationErrors).forEach((field) => {
        trackError(getStepForApiField(field) || submitStep, field, validationErrors[field]?.code);
    });
}

// POST the device-geo observation captured during the city step. Skipped when
// nothing was captured. Never throws and never blocks the signup redirect.
function reportGeoObservation(state, userId) {
    try {
        if (!state.geoObservation) {
            return;
        }
        window.ApiClient?.postGeoObservation(state.geoObservation, { signup_context: true })
            ?.catch(() => {});
        if (userId != null) {
            recordSignupCapture(String(userId));
        }
    } catch (e) {
        // Recording an observation must never affect signup.
    }
}

// The stored account names the new user. Otherwise the next page reads the
// token as another user's and mints again.
function storeAuthToken(result) {
    clearJwtToken();
    setJwtToken({
        ...(getJwtToken() || {}),
        auth: { token: result.token },
        account: { userId: result.account?.id != null ? String(result.account.id) : undefined },
    });
}
